/*
  FullStory Session Sampling + Daily Cap
 
  Controls FullStory data capture using two layers:
 
  1. Daily session cap — limits total captured sessions to DAILY_SESSION_LIMIT
     per calendar day. Counter is stored in a cookie and resets at midnight.
     This keeps monthly usage predictable (10k/day × 30 days = ~300k sessions).
 
  2. User sample rate — remembers each user's capture decision for SAMPLE_DAYS
     so returning users get a consistent experience. Decision is made randomly
     on first visit based on SAMPLE_RATE percentage.
 
  Gate order:
    Daily cap check → User sample check → Load FullStory
 
  Config:
    DAILY_SESSION_LIMIT  — max sessions to capture per day (default: 10,000)
    SAMPLE_RATE          — % of users to capture if under daily cap (default: 10%)
    SAMPLE_DAYS          — how long to remember a user's sample decision (default: 90 days)
 
  Cookies:
    _fs_capture_date  — today's date (YYYY-MM-DD), resets counter on new day
    _fs_daily_count   — session count for the current day
    _fs_sample_user   — whether this user is in the sample (true/false)
 */

(() => {
  'use strict';

  // Config
  const DAILY_SESSION_LIMIT = 10000;
  const COOKIE_SAMPLE_NAME  = '_fs_sample_user';
  const COOKIE_COUNT_NAME   = '_fs_daily_count';
  const COOKIE_DATE_NAME    = '_fs_capture_date';
  const SAMPLE_RATE         = 10;  // % of users to capture
  const SAMPLE_DAYS         = 90;  // days to persist sample decision

  // Helpers
  const getCookieValue = (name) => {
    try {
      const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  };

  const setCookie = (name, value, days, sameSite = 'Lax') => {
    try {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${encodeURIComponent(value)}; expires=${date.toUTCString()}; path=/; SameSite=${sameSite}`;
    } catch (e) {
      console.error('FullStory cookie write failed', e);
    }
  };

  // Daily cap check
  const isDailyCapReached = () => {
    try {
      const today       = new Date().toISOString().slice(0, 10);
      const storedDate  = getCookieValue(COOKIE_DATE_NAME);
      const storedCount = parseInt(getCookieValue(COOKIE_COUNT_NAME), 10) || 0;

      if (storedDate !== today) {
        setCookie(COOKIE_DATE_NAME, today, 1);
        setCookie(COOKIE_COUNT_NAME, '1', 1);
        return false;
      }

      if (storedCount >= DAILY_SESSION_LIMIT) return true;

      setCookie(COOKIE_COUNT_NAME, String(storedCount + 1), 1);
      return false;

    } catch (e) {
      console.error('FullStory daily cap check failed', e);
      return false;
    }
  };

  // User sample check
  const shouldSampleUser = (rate, days) => {
    try {
      const existing = getCookieValue(COOKIE_SAMPLE_NAME);

      if (existing === 'true')  return true;
      if (existing === 'false') return false;

      const sample = Math.random() < rate / 100;
      setCookie(COOKIE_SAMPLE_NAME, String(sample), days);
      return sample;

    } catch (e) {
      console.error('FullStory sampling failed', e);
      return false;
    }
  };

  // Gate checks
  if (isDailyCapReached()) return;
  if (!shouldSampleUser(SAMPLE_RATE, SAMPLE_DAYS)) return;

  // FullStory config
  window._fs_host      = 'fullstory.com';
  window._fs_script    = 'edge.fullstory.com/s/fs.js';
  window._fs_org       = 'o-24CGJQ-na1';
  window._fs_namespace = 'FS';

  // FullStory loader
  ((win, doc, namespace, tag) => {
    if (namespace in win) {
      win.console?.log('FullStory namespace conflict. Please set window["_fs_namespace"].');
      return;
    }

    const fs = win[namespace] = (...args) => {
      fs.q ? fs.q.push(args) : fs._api(...args);
    };

    fs.q = [];

    const script = doc.createElement(tag);
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.src = `https://${window._fs_script}`;

    const firstScript = doc.getElementsByTagName(tag)[0];
    firstScript.parentNode.insertBefore(script, firstScript);

  })(window, document, window._fs_namespace, 'script');

})();